import {
  JourneyFeatureFlags,
  journeyStorage,
  postMessage
} from '@epilot/journey-logic-commons'
import { appEventsSchema } from '@epilot/journey-logic-commons/src/schemas/v4/generated/events.zod'
import type { AppEvents } from '@epilot/journey-logic-commons/src/types/next'
import {
  APP_EVENT,
  KNOWN_EVENT_SENDERS
} from '@epilot/journey-logic-commons/src/types/next'
import { useEffect, useState } from 'react'

import { usePortalCheck } from '../blocks-renderers'
import { toggleDarkMode } from '../store/actions/toggleDarkMode'
import { selectOriginalJourneyOrRoot } from '../store/selectors/config'
import {
  selectAppParamsOrThrows,
  selectDarkMode,
  selectIsPreview
} from '../store/selectors/runtime'
import { debug } from '../utils/debug'

import { useAppStoreSelector } from './useAppStoreSelector'
import { useFlag } from './useFlags'
import { useSystemDarkMode } from './useSystemDarkMode'

const JOURNEY_DARK_MODE_STORAGE_PREFIX = 'journey-dark-mode-'

export const useDarkModeInitialization = () => {
  const darkMode = useAppStoreSelector(selectDarkMode)
  const journey = useAppStoreSelector(selectOriginalJourneyOrRoot)
  const systemPrefersDark = useSystemDarkMode()
  const isPreview = useAppStoreSelector(selectIsPreview)
  const { isECPPortal } = usePortalCheck()
  const { isEmbedded } = useAppStoreSelector(selectAppParamsOrThrows)
  const journeyId = journey?.journeyId
  const isDarkModeEnabled = journey?.settings?.enableDarkMode
  const isDareModeFeatureFlagEnabled = useFlag(JourneyFeatureFlags.DARK_MODE)
  const isJourneyEmbedded = !isPreview && isEmbedded

  const [ecpThemePreference, setEcpThemePreference] = useState<boolean | null>(
    null
  )

  // Listen for theme changes from parent ECP portal
  useEffect(() => {
    if (!isDareModeFeatureFlagEnabled) {
      return
    }

    const handleMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') {
        return
      }

      const parsed = appEventsSchema.safeParse(event.data)

      if (!parsed.success) {
        return
      }

      const castedMessage = parsed.data

      // Only handle messages from ECP portal
      if (castedMessage.from !== KNOWN_EVENT_SENDERS.ECP_PORTAL) {
        return
      }

      if (castedMessage.type === APP_EVENT.THEME_CHANGED) {
        debug('[Journey Theme] Received theme change:', castedMessage)
        setEcpThemePreference(castedMessage.payload.isDarkMode)
      }
    }

    // Request current theme from parent when journey loads
    if (isJourneyEmbedded) {
      debug('[Journey Theme] Requesting current theme from parent')
      postMessage<AppEvents>(
        {
          type: APP_EVENT.REQUEST_CURRENT_THEME,
          from: KNOWN_EVENT_SENDERS.JOURNEY_APP
        },
        window.parent,
        {
          targetOrigin: '*'
        }
      )
    }

    window.addEventListener('message', handleMessage)

    return () => window.removeEventListener('message', handleMessage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Get ECP theme preference - primarily from postMessage, fallback to system preference
  const ecpDarkModePreference =
    ecpThemePreference !== null ? ecpThemePreference : systemPrefersDark

  useEffect(() => {
    if (!isDareModeFeatureFlagEnabled) {
      return
    }

    // If opened from ECP, respect parent's theme preference
    if (isECPPortal) {
      if (ecpDarkModePreference !== darkMode) {
        debug('[Journey Theme] Setting dark mode to:', ecpDarkModePreference)
        toggleDarkMode(ecpDarkModePreference)
      }

      return
    }

    // If embedded but not in ECP, respect system settings
    if (isJourneyEmbedded && !isECPPortal) {
      if (systemPrefersDark !== darkMode) {
        debug('[Journey Theme] Setting dark mode to:', systemPrefersDark)
        toggleDarkMode(systemPrefersDark)
      }

      return
    }

    if (!journeyId || !isDarkModeEnabled || isPreview) {
      return
    }

    const storageKey = `${JOURNEY_DARK_MODE_STORAGE_PREFIX}${journeyId}`
    const savedPreference = journeyStorage.getItem(storageKey)

    if (savedPreference === null) {
      toggleDarkMode(systemPrefersDark)
    } else {
      const userPrefersDark = savedPreference === 'true'

      if (userPrefersDark !== darkMode) {
        toggleDarkMode(userPrefersDark)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    journeyId,
    isDarkModeEnabled,
    systemPrefersDark,
    isECPPortal,
    ecpThemePreference
  ])
}

export const useDarkModeToggle = () => {
  const darkMode = useAppStoreSelector(selectDarkMode)
  const journey = useAppStoreSelector(selectOriginalJourneyOrRoot)
  const journeyId = journey?.journeyId

  return () => {
    const newDarkMode = !darkMode

    if (journeyId) {
      const storageKey = `${JOURNEY_DARK_MODE_STORAGE_PREFIX}${journeyId}`

      journeyStorage.setItem(storageKey, newDarkMode.toString())
    }

    toggleDarkMode(undefined)
  }
}
